
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { User2Icon, LogOut, Lightbulb, MessageCircleQuestionMark, Sun, Moon, MoreHorizontal } from 'lucide-react';
import LogoDark from '../../assets/logo/Logo.png';
import LogoLight from '../../assets/logo/logo-dark.png';
import useTheme from '../../hooks/useTheme';
import CreditsIndicator from './CreditsIndicator';

const DesktopNavigation = ({
  navigation,
  isCurrentPath,
  identity,
  onLogout,
  onStartTour,
  contactSupportUrl,
}) => {
  const { isDark, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  return (
    <aside className='hidden lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:flex lg:flex-col sidebar z-30'>
      {/* Logo */}
      <div className='h-16 flex items-center px-5 shrink-0' style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
        <img src={isDark ? LogoDark : LogoLight} alt='Postreams Logo' className='h-9 w-auto' />
      </div>

      {/* Nav links */}
      <nav className='flex-1 px-3 py-5 space-y-0.5 overflow-y-auto thin-scrollbar' data-tour='nav-links'>
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = isCurrentPath(item.href);
          const navLabel = item.label || item.name;

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`desktop-tour-${item.name
                .replace(/\s+/g, '-')
                .toLowerCase()} sidebar-link relative ${isActive ? 'active' : ''}`}
              onClick={(e) => {
                if (item.onClick) {
                  e.preventDefault();
                  item.onClick();
                }
              }}
            >
              <Icon className='sidebar-icon h-4.5 w-4.5' style={{ width: '1.125rem', height: '1.125rem' }} />
              <span>{navLabel}</span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className='p-3 space-y-2' style={{ borderTop: '1px solid var(--sidebar-border)' }}>
        {/* Post credits */}
        <CreditsIndicator />

        <button
          type='button'
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className='flex w-full items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer text-left'
          style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
        >
          <div
            className='w-8 h-8 rounded-full flex items-center justify-center shrink-0'
            style={{ background: 'var(--bg-card-alt)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
          >
            {isDark ? (
              <Sun style={{ width: '1rem', height: '1rem' }} />
            ) : (
              <Moon style={{ width: '1rem', height: '1rem' }} />
            )}
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium truncate'>
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </p>
            <p className='text-xs' style={{ color: 'var(--text-ghost)' }}>
              Switch appearance
            </p>
          </div>
        </button>

        {/* User info + Logout */}
        <div
          className='flex items-center gap-3 px-3 py-2.5 rounded-xl'
          style={{ background: 'var(--bg-surface)' }}
        >
          <div
            className='w-8 h-8 rounded-full flex items-center justify-center shrink-0'
            style={{ background: 'var(--bg-card-alt)', border: '1px solid var(--border)' }}
          >
            <User2Icon style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium truncate' style={{ color: 'var(--text-primary)' }}>
              {identity?.full_name || identity?.email || 'User'}
            </p>
            <p className='text-xs' style={{ color: 'var(--text-ghost)' }}>
              {format(new Date(), 'MMM do yyyy', { locale: enUS })}
            </p>
          </div>
          <div className='flex items-center gap-1 shrink-0' ref={menuRef}>
            <div className='relative'>
              <button
                type='button'
                onClick={() => setMenuOpen((v) => !v)}
                aria-label='More options'
                aria-expanded={menuOpen}
                className='flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer'
                style={{ background: 'transparent', color: 'var(--text-muted)' }}
              >
                <MoreHorizontal style={{ width: '1rem', height: '1rem' }} />
              </button>
              {menuOpen && (
                <div
                  role='menu'
                  className='absolute bottom-full right-0 mb-2 w-52 translate-x-2.5 rounded-xl py-1 shadow-lg z-50'
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
                >
                  <button
                    type='button'
                    onClick={() => { setMenuOpen(false); window.location.href = contactSupportUrl; }}
                    className='w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-[var(--bg-surface)] cursor-pointer'
                    style={{ color: 'var(--text-primary)' }}
                    role='menuitem'
                  >
                    <MessageCircleQuestionMark style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
                    <span>Contact Support</span>
                  </button>
                  <button
                    type='button'
                    onClick={() => { setMenuOpen(false); toggleTheme(); }}
                    className='w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-[var(--bg-surface)] cursor-pointer'
                    style={{ color: 'var(--text-primary)' }}
                    role='menuitem'
                  >
                    {isDark ? (
                      <Sun style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
                    ) : (
                      <Moon style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
                    )}
                    <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                </div>
              )}
            </div>
            <button
              type='button'
              onClick={onLogout}
              aria-label='Log out'
              className='flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer'
              style={{ background: 'transparent', color: 'var(--text-muted)' }}
            >
              <LogOut style={{ width: '1rem', height: '1rem' }} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default DesktopNavigation;
